import { Component, lazy, Suspense, useState, type ErrorInfo, type ReactNode } from 'react';
import { Alert, Button, Skeleton, Tabs, type TabItem } from '@tb/ui';
import type { CustomSource } from '@tb/shared';
import { useT } from '../../i18n/LocaleProvider';
import type { FileKind } from './CodeMirrorPane';

const CodeMirrorPane = lazy(() => import('./CodeMirrorPane'));

const TABS: TabItem<FileKind>[] = [
  { id: 'html', label: 'HTML', icon: 'code' },
  { id: 'css', label: 'CSS', icon: 'palette' },
  { id: 'js', label: 'JS', icon: 'file' },
];

export interface CodeEditorProps {
  source: CustomSource;
  onChange: (source: CustomSource) => void;
  dark: boolean;
  notice?: ReactNode;
}

export function CodeEditor({ source, onChange, dark, notice }: CodeEditorProps) {
  const [tab, setTab] = useState<FileKind>('html');
  const t = useT();

  return (
    <div className="tb-stack">
      <Tabs items={TABS} value={tab} onChange={setTab} ariaLabel="Source files" />

      {notice}

      <Alert tone="info">{t('editor.code.assetHint')}</Alert>

      <div className="code-pane">
        <ChunkBoundary>
          <Suspense fallback={<Skeleton height="100%" />}>
            <CodeMirrorPane
              key={tab}
              value={source[tab]}
              kind={tab}
              dark={dark}
              onChange={(next) => onChange({ ...source, [tab]: next })}
            />
          </Suspense>
        </ChunkBoundary>
      </div>
    </div>
  );
}

class ChunkBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('The code editor failed to load', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return <ChunkFailure onRetry={() => this.setState({ failed: false })} />;
  }
}

function ChunkFailure({ onRetry }: { onRetry: () => void }) {
  const t = useT();
  return (
    <div className="tb-stack" style={{ padding: 'var(--tb-space-4)' }}>
      <Alert tone="danger" title={t('editor.code.loadFailed')}>
        {t('editor.code.loadFailedBody')}
      </Alert>
      <Button icon="refresh" size="sm" onClick={onRetry}>
        {t('editor.code.retry')}
      </Button>
    </div>
  );
}
